/**
 * Pacote 3 (P3-27 a P3-32) — regra de cobrança da Locação POR PARCEIRO e projeção das parcelas.
 * Compartilhado entre servidor e telas; sem dependências de Node.
 *
 * A regra é dado do parceiro (copiada para a versão junto com as Condições de Locação). O que o contrato do parceiro
 * não define fica nulo e aparece como "A confirmar": o HUB nunca inventa prazo, data, desconto ou percentual.
 * Exemplo aprovado (RB, minuta cl. 2.2, 2.5 e 3.1): 1ª emissão 10 dias após a remessa (fim de semana ou feriado:
 * 1º dia útil seguinte); 1º vencimento 20 dias corridos após a emissão; vencimentos seguintes no mesmo dia do mês;
 * prazo contado de cada NF de remessa. Emissão das seguintes, vencimento em fim de semana e mês sem o dia: A confirmar.
 *
 * Contas a Receber E1 (23/10/2026): o MESMO registro guarda a regra de RECEBIMENTO da comissão do parceiro
 * (`recebimento`: liberação, repasse, confirmação) — sem cadastro novo. Vale para qualquer papel do parceiro.
 */
import { normalizarRegraRecebimento, regraRecebimentoVaziaDe, type RegraRecebimento } from './receber.ts';

export interface RegraCobrancaLocacao {
  /** dias corridos entre a remessa (NF) e a 1ª emissão; null = A confirmar */
  diasEmissaoAposRemessa: number | null;
  /** 1ª emissão em fim de semana ou feriado vai para o 1º dia útil seguinte (true), não se desloca (false) ou A confirmar (null) */
  emissaoFimDeSemanaProximoDiaUtil: boolean | null;
  /** dias corridos entre a emissão e o 1º vencimento; null = A confirmar */
  diasVencimentoAposEmissao: number | null;
  /** vencimentos seguintes no mesmo dia do mês do 1º vencimento (true) ou A confirmar (null) */
  vencimentosSeguintesMesmoDia: boolean | null;
  /** textos do contrato para os pontos ainda não definidos ("" = A confirmar) */
  emissaoSeguintes: string;
  vencimentoFimDeSemana: string;
  mesSemDia: string;
  nfInttrix: string;
  prazoRepasse: string;
  descontosRetencoes: string;
  inadimplencia: string;
  pagamentoParcial: string;
  devolucao: string;
  renovacao: string;
  reajusteMargem: string;
  /** Contas a Receber E1: regra de recebimento da comissão (ausente = nada definido) */
  recebimento?: RegraRecebimento;
}

export const A_CONFIRMAR = 'A confirmar';

export const regraCobrancaVazia = (): RegraCobrancaLocacao => ({
  diasEmissaoAposRemessa: null,
  emissaoFimDeSemanaProximoDiaUtil: null,
  diasVencimentoAposEmissao: null,
  vencimentosSeguintesMesmoDia: null,
  emissaoSeguintes: '',
  vencimentoFimDeSemana: '',
  mesSemDia: '',
  nfInttrix: '',
  prazoRepasse: '',
  descontosRetencoes: '',
  inadimplencia: '',
  pagamentoParcial: '',
  devolucao: '',
  renovacao: '',
  reajusteMargem: '',
});

/** Campos de texto da regra (rótulo para tela; "" aparece como A confirmar). */
export const CAMPOS_TEXTO_REGRA: { chave: keyof RegraCobrancaLocacao; rotulo: string }[] = [
  { chave: 'emissaoSeguintes', rotulo: 'Emissão das cobranças seguintes' },
  { chave: 'vencimentoFimDeSemana', rotulo: 'Vencimento em fim de semana ou feriado' },
  { chave: 'mesSemDia', rotulo: 'Mês sem o dia do vencimento' },
  { chave: 'nfInttrix', rotulo: 'NF da Inttrix' },
  { chave: 'prazoRepasse', rotulo: 'Prazo de repasse à Inttrix' },
  { chave: 'descontosRetencoes', rotulo: 'Descontos e retenções' },
  { chave: 'inadimplencia', rotulo: 'Inadimplência' },
  { chave: 'pagamentoParcial', rotulo: 'Pagamento parcial' },
  { chave: 'devolucao', rotulo: 'Devolução' },
  { chave: 'renovacao', rotulo: 'Renovação' },
  { chave: 'reajusteMargem', rotulo: 'Reajuste sobre a margem' },
];

const inteiroOuNulo = (x: unknown, max: number): number | null => (typeof x === 'number' && Number.isInteger(x) && x >= 0 && x <= max ? x : null);
const boolOuNulo = (x: unknown): boolean | null => (x === true || x === false ? x : null);
const texto = (x: unknown) => (typeof x === 'string' ? x.trim().slice(0, 600) : '');

/** Normaliza o que vier da tela ou do banco (valores fora do formato viram "A confirmar"). */
export function normalizarRegraCobranca(v: unknown): RegraCobrancaLocacao {
  const o = v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
  const r = regraCobrancaVazia();
  r.diasEmissaoAposRemessa = inteiroOuNulo(o.diasEmissaoAposRemessa, 365);
  r.emissaoFimDeSemanaProximoDiaUtil = boolOuNulo(o.emissaoFimDeSemanaProximoDiaUtil);
  r.diasVencimentoAposEmissao = inteiroOuNulo(o.diasVencimentoAposEmissao, 365);
  r.vencimentosSeguintesMesmoDia = boolOuNulo(o.vencimentosSeguintesMesmoDia);
  for (const { chave } of CAMPOS_TEXTO_REGRA) (r as unknown as Record<string, unknown>)[chave] = texto(o[chave]);
  if (o.recebimento !== undefined && o.recebimento !== null) {
    const rec = normalizarRegraRecebimento(o.recebimento);
    if (!regraRecebimentoVaziaDe(rec)) r.recebimento = rec;
  }
  return r;
}

/** A regra tem algo da COBRANÇA da Locação definido (o recebimento da comissão sozinho não conta). */
export function temRegraCobrancaLocacao(r: RegraCobrancaLocacao | null | undefined): boolean {
  if (!r) return false;
  const vazia = regraCobrancaVazia();
  return (Object.keys(vazia) as (keyof RegraCobrancaLocacao)[]).some((k) => r[k] !== vazia[k]);
}

/** Textos para o PDF ao Cliente: só o que está definido (o "A confirmar" é interno). */
export function textosCobrancaCliente(r?: RegraCobrancaLocacao | null): { titulo: string; texto: string }[] {
  const out: { titulo: string; texto: string }[] = [{ titulo: 'Início da locação', texto: 'O prazo de locação é contado a partir da emissão de cada nota fiscal de remessa dos equipamentos.' }];
  if (!r) return out;
  if (r.diasEmissaoAposRemessa !== null || r.diasVencimentoAposEmissao !== null) {
    const partes = [
      r.diasEmissaoAposRemessa !== null ? `Emitida ${r.diasEmissaoAposRemessa} dias após a remessa dos equipamentos` : '',
      r.diasVencimentoAposEmissao !== null ? `com vencimento em ${r.diasVencimentoAposEmissao} dias corridos após sua emissão` : '',
    ].filter(Boolean);
    out.push({ titulo: 'Primeira fatura', texto: `${partes.join(', ')}.` });
  }
  if (r.vencimentosSeguintesMesmoDia) out.push({ titulo: 'Pagamentos seguintes', texto: 'Vencimento na mesma data dos meses subsequentes.' });
  return out;
}

// ---------------------------------------------------------------- datas
const RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const utc = (s: string) => {
  const m = RE.exec(s);
  return m ? new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))) : null;
};
const iso = (d: Date) => d.toISOString().slice(0, 10);
export const somarDias = (s: string, n: number) => {
  const d = utc(s);
  return d ? iso(new Date(d.getTime() + n * 86_400_000)) : '';
};
export const fimDeSemana = (s: string) => {
  const d = utc(s);
  return d ? d.getUTCDay() === 0 || d.getUTCDay() === 6 : false;
};
/** Dia útil seguinte (sábado/domingo; feriados não são conhecidos pelo HUB e ficam a cargo do registro efetivo). */
export function proximoDiaUtil(s: string) {
  let x = s;
  while (fimDeSemana(x)) x = somarDias(x, 1);
  return x;
}
/** Mesma data N meses depois; se o mês não tiver o dia, devolve null (o HUB não inventa a data). */
export function mesmoDiaMeses(s: string, meses: number): string | null {
  const d = utc(s);
  if (!d) return null;
  const alvo = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + meses, d.getUTCDate()));
  return alvo.getUTCDate() === d.getUTCDate() ? iso(alvo) : null;
}

export type OrigemData = 'informada' | 'prevista' | 'a_confirmar';
export interface DataParcela {
  data: string | null;
  origem: OrigemData;
  /** motivo da pendência quando 'a_confirmar' ou quando a data prevista cai em fim de semana */
  pendencia?: string;
}
export interface ParcelaProjetada {
  numero: number;
  emissao: DataParcela;
  vencimento: DataParcela;
}

/**
 * Projeção das parcelas de UMA remessa pela regra do parceiro. `informadas` traz as datas efetivas já registradas
 * (por número da parcela); elas prevalecem sobre a projeção. Nada é inventado: sem regra, a data fica "a confirmar".
 */
export function projetarParcelas(regra: RegraCobrancaLocacao | null | undefined, dataRemessa: string, quantidade: number, informadas: Record<number, { emissao?: string | null; vencimento?: string | null }> = {}): ParcelaProjetada[] {
  const r = regra ?? regraCobrancaVazia();
  const out: ParcelaProjetada[] = [];
  // 1ª emissão e 1º vencimento (pela regra), salvo data informada
  let emissao1: DataParcela;
  if (informadas[1]?.emissao) emissao1 = { data: informadas[1].emissao, origem: 'informada' };
  else if (r.diasEmissaoAposRemessa === null || !RE.test(dataRemessa)) emissao1 = { data: null, origem: 'a_confirmar', pendencia: 'regra de emissão não definida' };
  else {
    const bruta = somarDias(dataRemessa, r.diasEmissaoAposRemessa);
    if (fimDeSemana(bruta) && r.emissaoFimDeSemanaProximoDiaUtil === true) emissao1 = { data: proximoDiaUtil(bruta), origem: 'prevista' };
    else if (fimDeSemana(bruta) && r.emissaoFimDeSemanaProximoDiaUtil === null) emissao1 = { data: bruta, origem: 'prevista', pendencia: 'fim de semana: a confirmar' };
    else emissao1 = { data: bruta, origem: 'prevista' };
  }
  let venc1: DataParcela;
  if (informadas[1]?.vencimento) venc1 = { data: informadas[1].vencimento, origem: 'informada' };
  else if (r.diasVencimentoAposEmissao === null || !emissao1.data) venc1 = { data: null, origem: 'a_confirmar', pendencia: 'regra de vencimento não definida' };
  else {
    const v = somarDias(emissao1.data, r.diasVencimentoAposEmissao);
    venc1 = fimDeSemana(v) && !r.vencimentoFimDeSemana ? { data: v, origem: 'prevista', pendencia: 'fim de semana: a confirmar' } : { data: v, origem: 'prevista' };
  }
  out.push({ numero: 1, emissao: emissao1, vencimento: venc1 });
  // seguintes: vencimento no mesmo dia do mês do 1º vencimento; emissão das seguintes só quando informada
  for (let n = 2; n <= quantidade; n++) {
    const inf = informadas[n] ?? {};
    const emissao: DataParcela = inf.emissao ? { data: inf.emissao, origem: 'informada' } : { data: null, origem: 'a_confirmar', pendencia: r.emissaoSeguintes ? undefined : 'emissão das seguintes: a confirmar' };
    let vencimento: DataParcela;
    if (inf.vencimento) vencimento = { data: inf.vencimento, origem: 'informada' };
    else if (r.vencimentosSeguintesMesmoDia !== true || !venc1.data) vencimento = { data: null, origem: 'a_confirmar', pendencia: 'regra dos vencimentos seguintes não definida' };
    else {
      const v = mesmoDiaMeses(venc1.data, n - 1);
      if (v === null) vencimento = { data: null, origem: 'a_confirmar', pendencia: 'mês sem o dia: a confirmar' };
      else vencimento = fimDeSemana(v) && !r.vencimentoFimDeSemana ? { data: v, origem: 'prevista', pendencia: 'fim de semana: a confirmar' } : { data: v, origem: 'prevista' };
    }
    out.push({ numero: n, emissao, vencimento });
  }
  return out;
}

/** Margem da parcela = base elegível da parcela × percentual contratado (da proposta aceita), arredondada a centavos. */
export const margemDaParcela = (baseElegivel: number, percentualContratado: number) => Math.round(baseElegivel * percentualContratado) / 100;

// ---------------------------------------------------------------- acompanhamento da OL (P3-29 a P3-32)

/** Etapas contratuais e operacionais (fatos com data). "Proposta aceita" vem do Pedido; "Remessa" vem da 1ª NF de remessa. */
export const ETAPAS_LOCACAO = [
  { id: 'proposta_aceita', rotulo: 'Proposta aceita', automatica: true },
  { id: 'po_enviada', rotulo: 'PO/OL enviada ao parceiro', automatica: false },
  { id: 'analise_credito', rotulo: 'Análise de crédito', automatica: false },
  { id: 'credito_aprovado', rotulo: 'Crédito aprovado', automatica: false },
  { id: 'contrato_enviado', rotulo: 'Contrato enviado', automatica: false },
  { id: 'assinatura_conferida', rotulo: 'Assinatura conferida', automatica: false },
  // P4 bloco 3 (I13): liberação conforme estoque, antes da remessa
  { id: 'liberacao', rotulo: 'Liberação', automatica: false },
  { id: 'remessa', rotulo: 'Remessa / NF', automatica: true },
  { id: 'implantacao', rotulo: 'Implantação', automatica: false },
] as const;
export type IdEtapaLocacao = (typeof ETAPAS_LOCACAO)[number]['id'] | 'credito_recusado' | 'encerramento';
export const ROTULO_ETAPA: Record<IdEtapaLocacao, string> = { ...Object.fromEntries(ETAPAS_LOCACAO.map((e) => [e.id, e.rotulo])), credito_recusado: 'Crédito recusado', encerramento: 'Encerramento' } as Record<IdEtapaLocacao, string>;
/** Etapas que a pessoa registra (as automáticas vêm do Pedido e da remessa). */
export const ETAPAS_REGISTRAVEIS: IdEtapaLocacao[] = ['po_enviada', 'analise_credito', 'credito_aprovado', 'credito_recusado', 'contrato_enviado', 'assinatura_conferida', 'liberacao', 'implantacao', 'encerramento'];

export interface EtapaLinha {
  id: IdEtapaLocacao;
  rotulo: string;
  data: string | null;
  situacao: 'concluida' | 'em_andamento' | 'nao_informada';
}
/**
 * Linha do tempo das etapas e a etapa atual. Nada é presumido: etapa sem fato registrado aparece "Não informado";
 * a etapa em andamento é a primeira ainda não concluída depois da última concluída (a análise de crédito, quando
 * registrada sem aprovação, fica "em andamento desde" a data registrada).
 */
export function linhaDoTempo(fatos: { etapa: IdEtapaLocacao; data: string }[]): { etapas: EtapaLinha[]; atual: string; tom: 'ok' | 'pend' | 'alerta' | 'ni' } {
  const data = (id: IdEtapaLocacao) => fatos.filter((f) => f.etapa === id).map((f) => f.data).sort().at(-1) ?? null;
  const etapas: EtapaLinha[] = ETAPAS_LOCACAO.map((e) => ({ id: e.id, rotulo: e.rotulo, data: data(e.id), situacao: data(e.id) ? 'concluida' : 'nao_informada' }));
  // a análise de crédito só "conclui" com a aprovação (ou a recusa); registrada sozinha, está em andamento
  const analise = etapas.find((e) => e.id === 'analise_credito')!;
  if (analise.data && !data('credito_aprovado') && !data('credito_recusado')) analise.situacao = 'em_andamento';
  if (data('encerramento')) return { etapas, atual: 'Encerrada', tom: 'ni' };
  if (data('credito_recusado')) return { etapas, atual: 'Crédito recusado', tom: 'alerta' };
  if (data('implantacao')) return { etapas, atual: 'Locação ativa', tom: 'ok' };
  if (analise.situacao === 'em_andamento') return { etapas, atual: 'Análise de crédito', tom: 'pend' };
  const ultima = [...etapas].reverse().find((e) => e.situacao === 'concluida');
  const proxima = ultima ? etapas[etapas.indexOf(ultima) + 1] : etapas[0];
  if (proxima && proxima.situacao === 'nao_informada') proxima.situacao = 'em_andamento';
  return { etapas, atual: proxima ? proxima.rotulo : (ultima?.rotulo ?? 'Não informado'), tom: 'pend' };
}

export interface MovimentoParcela {
  tipo: 'pagamento_cliente' | 'recebimento_inttrix';
  data: string;
  valor: number;
  estornado: boolean;
}
export type SituacaoParcela = 'Prevista' | 'Pagamento não informado' | 'Parcial' | 'Recebimento não informado' | 'Recebimento parcial' | 'Paga e recebida';
/**
 * Situação da parcela: pagamento do Cliente e recebimento da Inttrix são registros distintos; parcial e estorno
 * nunca marcam a parcela como integralmente paga ou recebida. O aluguel pago ao parceiro não é receita da Inttrix.
 */
export function situacaoParcela(aluguel: number, margem: number, vencimento: string | null, movimentos: MovimentoParcela[], hoje: string) {
  const validos = movimentos.filter((m) => !m.estornado);
  const pago = Math.round(validos.filter((m) => m.tipo === 'pagamento_cliente').reduce((s, m) => s + m.valor, 0) * 100) / 100;
  const recebido = Math.round(validos.filter((m) => m.tipo === 'recebimento_inttrix').reduce((s, m) => s + m.valor, 0) * 100) / 100;
  let situacao: SituacaoParcela;
  let saldo = 0;
  if (pago === 0) situacao = vencimento && vencimento < hoje ? 'Pagamento não informado' : 'Prevista';
  else if (pago < aluguel) {
    situacao = 'Parcial';
    saldo = Math.round((aluguel - pago) * 100) / 100;
  } else if (recebido === 0) situacao = 'Recebimento não informado';
  else if (recebido < margem) situacao = 'Recebimento parcial';
  else situacao = 'Paga e recebida';
  return { situacao, pago, recebido, saldo };
}

/** Itens do dossiê da PO/OL e quantos documentos restritos cada um pede (procurador: um por unidade da operação). */
export const ITENS_DOSSIE = [
  { id: 'contrato_social', rotulo: 'Contrato social', exigidos: 1 },
  { id: 'procurador', rotulo: 'E-mail do procurador de cada unidade', exigidos: 0 },
  { id: 'testemunha', rotulo: 'Testemunhas (nome e e-mail)', exigidos: 2 },
  { id: 'referencia_comercial', rotulo: 'Referências comerciais (nome e telefone ou e-mail)', exigidos: 3 },
  { id: 'referencia_bancaria', rotulo: 'Referência bancária', exigidos: 1 },
] as const;
export type IdItemDossie = (typeof ITENS_DOSSIE)[number]['id'];
